import EN from '../../../../EN.json';
import FR from '../../../../FR.json';
import { addProductVulcan } from '../customerInformation/customerInformationFieldNames';
import { BUNDLED_MONTHLY_RATE } from './businessWifiFieldNames';

/**
 * Business Wi-Fi bundled-rate resolver.
 *
 * Business Wi-Fi carries TWO Contentful-authored monthly prices, both $20:
 *  1. the `addProductVulcan` option price (covers the first access point), and
 *  2. `numberOfAccessPoints.monthlyPayment` (each access point beyond the first).
 *
 * Selected alongside any other product, the first access point becomes $30;
 * each additional access point stays at $20. This module resolves that rate and
 * patches the option price, and is called at the top of the three exported
 * functions in vulcanPricing.js so the legacy and forms-core runtimes are both
 * covered.
 *
 * Product selection values are LOCALIZED ('Business Wifi' / 'Wi-Fi Affaires'),
 * and each order is priced twice — once in the representative's locale and once
 * in the opposite one, because formStepFMVulcanUtils persists both
 * `priceSummary` and `priceSummaryOfAlternateLanguage` and the contract document
 * is generated from them. Matching a single English literal would leave French
 * orders at $20 and emit a $20 French contract for every English order, so
 * identity is a set membership test, never an equality check.
 */
const BUSINESS_WIFI_VALUES = new Set(
  [EN, FR]
    .map(translations => translations.vulcanWebform.vulBusinessWifi)
    .filter(Boolean)
);

const getSelectedProducts = formFields => {
  const field = formFields && formFields[addProductVulcan];
  return field ? field.value : undefined;
};

export const isBusinessWifiBundled = formFields => {
  const selected = getSelectedProducts(formFields);
  return (
    Array.isArray(selected) &&
    selected.length > 1 &&
    selected.some(product => BUSINESS_WIFI_VALUES.has(product))
  );
};

/**
 * Contentful authors the price into the label text itself, e.g.
 * "Number of Access Points ($20)" / "Nombre de points d'accès (20 $)".
 * Matches an English-style "$20" and a French-style "20 $" (the separating
 * space may be a non-breaking space, which \s covers), and rewrites only the
 * amount that equals the standalone rate — "$120" or "Maximum 3" are untouched.
 */
const CURRENCY_AMOUNT = /\$\s?(\d+(?:[.,]\d+)?)|(\d+(?:[.,]\d+)?)\s?\$/g;

const rewriteBundledAmount = (label, standaloneRate) => {
  const from = Number(standaloneRate);
  if (typeof label !== 'string' || !label) return label;
  if (!Number.isFinite(from) || from <= 0 || from === BUNDLED_MONTHLY_RATE) {
    return label;
  }

  return label.replace(
    CURRENCY_AMOUNT,
    (match, leadingSymbol, trailingSymbol) => {
      const amount =
        leadingSymbol === undefined ? trailingSymbol : leadingSymbol;
      if (Number(String(amount).replace(',', '.')) !== from) return match;
      // The amount equals an integer rate, so any decimals are zeros worth keeping.
      const decimals = String(amount).match(/([.,]\d+)$/);
      const replacement = `${BUNDLED_MONTHLY_RATE}${
        decimals ? decimals[1] : ''
      }`;
      return match.replace(String(amount), replacement);
    }
  );
};

/**
 * Corrects the price printed inside a rendered field's label when the order is
 * bundled. Returns the input unchanged when nothing needs rewriting.
 *
 * Deliberately pure: the Contentful `fields` objects are cached and shared
 * across renders, so mutating a label in place would strand it at $30 after the
 * representative removes the other product (FR-008). Re-deriving per render
 * keeps the label in step with the selection.
 *
 * The access-point label is not handled here: its bundled wording is built by
 * rules/fibreBusinessWifi.js, and its $20 additional price is correct as-is.
 *
 * Called once per field per render, so the guards are ordered cheapest-first: a
 * single name check rejects every other field before any work touches the
 * product selection.
 */
export const applyBusinessWifiBundleRateToLabels = (fieldData, formFields) => {
  if (!fieldData || fieldData.name !== addProductVulcan) return fieldData;
  if (!isBusinessWifiBundled(formFields)) return fieldData;

  if (!Array.isArray(fieldData.options)) return fieldData;

  let didRewrite = false;
  const options = fieldData.options.map(option => {
    const fields = option && option.fields;
    if (!fields || !BUSINESS_WIFI_VALUES.has(fields.value)) return option;

    const label = rewriteBundledAmount(fields.label, fields.monthlyPayment);
    if (label === fields.label) return option;

    didRewrite = true;
    return { ...option, fields: { ...fields, label } };
  });

  return didRewrite ? { ...fieldData, options } : fieldData;
};

const patchBusinessWifiOption = productField => {
  const options = productField && productField.options;
  if (!Array.isArray(options)) return productField;

  let didPatch = false;
  const patchedOptions = options.map(option => {
    const fields = option && option.fields;
    if (!fields || !BUSINESS_WIFI_VALUES.has(fields.value)) return option;
    didPatch = true;
    return {
      ...option,
      fields: { ...fields, monthlyPayment: BUNDLED_MONTHLY_RATE }
    };
  });

  return didPatch ? { ...productField, options: patchedOptions } : productField;
};

export const applyBusinessWifiBundleRate = formFields => {
  if (!isBusinessWifiBundled(formFields)) return formFields;

  return {
    ...formFields,
    [addProductVulcan]: patchBusinessWifiOption(formFields[addProductVulcan])
  };
};

export default applyBusinessWifiBundleRate;
